/**
 * Utilidades para el módulo de Servicios
 */

export { formatDateInput, dateToISO } from "../../utils/dateUtils";

/**
 * Formatea una hora en formato 24h a formato 12h (AM/PM)
 */
export const formatHour12 = (hour) => {
  if (!hour) return "—";
  const parts = hour.split(":");
  const hhNum = parseInt(parts[0], 10);
  if (Number.isNaN(hhNum)) return hour;
  const mm = parts[1] ? parts[1].slice(0, 2) : "00";
  const ampm = hhNum >= 12 ? "PM" : "AM";
  let h12 = hhNum % 12;
  if (h12 === 0) h12 = 12;
  return `${h12}:${mm} ${ampm}`;
};

/**
 * Obtiene el nombre del técnico desde un servicio
 * Maneja tanto objetos populados como IDs
 */
export const getTechnicianName = (service, technicians = []) => {
  if (!service) return "—";
  const t = service.technician;
  if (!t) return "—";
  
  if (typeof t === "object") {
    return t.name || t.nombre || (t._id ? String(t._id) : "—");
  }
  
  const found = technicians.find((x) => x._id === t || x.id === t);
  if (found) {
    return found.name || found.nombre || String(found._id || found.id);
  }
  
  return String(t);
};

/**
 * Normaliza el payload de servicio para el backend
 * Asegura que todos los campos requeridos sean strings
 * Para actualizaciones, preserva undefined para que el backend no los elimine
 */
export const normalizeServicePayload = (raw = {}) => {
  const result = {};

  if (raw.hasOwnProperty("type"))
    result.type = raw.type || "";

  if (raw.hasOwnProperty("status"))
    result.status = raw.status || "PENDING";

  if (raw.hasOwnProperty("notes"))
    result.notes = raw.notes || "";

  if (raw.hasOwnProperty("date") && raw.date !== undefined)
    result.date = raw.date || "";

  if (raw.hasOwnProperty("client") && raw.client !== undefined)
    result.client = raw.client;

  if (raw.hasOwnProperty("branch") && raw.branch !== undefined)
    result.branch = raw.branch;

  if (raw.hasOwnProperty("hour") && raw.hour !== undefined)
    result.hour = raw.hour || "";

  if (raw.hasOwnProperty("technician") && raw.technician !== undefined)
    result.technician = raw.technician || "";

  if (raw.hasOwnProperty("technicians") && raw.technicians !== undefined)
    result.technicians = Array.isArray(raw.technicians)
      ? raw.technicians
      : [];

  if (raw.hasOwnProperty("frequency") && raw.frequency !== undefined)
    result.frequency = raw.frequency || "";

  return result;
};

/**
 * Filtra servicios según criterios de búsqueda
 */
export const filterServices = (services, { search, tecnicoFilter, zonaFilter }) => {
  let data = [...services];
  
  if (search.trim() !== "") {
    const q = search.toLowerCase();
    data = data.filter(
      (s) =>
        (s.type || "").toString().toLowerCase().includes(q) ||
        (s.client?.name || "").toLowerCase().includes(q) ||
        (s.branch?.name || "").toLowerCase().includes(q) ||
        (s.technician?.name || "").toLowerCase().includes(q)
    );
  }
  
  if (tecnicoFilter !== "") {
    data = data.filter((s) => (s.technician?._id || s.technician) === tecnicoFilter);
  }
  
  if (zonaFilter !== "") {
    data = data.filter((s) => s.branch?.city === zonaFilter);
  }
  
  return data;
};

/**
 * Ordena servicios por columna especificada
 * @param {Array} services - Servicios a ordenar
 * @param {string} orderBy - Campo por el cual ordenar (status, type, client, branch, technician, date, hour)
 * @param {string} orderDirection - Dirección del ordenamiento (asc, desc)
 * @returns {Array} Servicios ordenados
 */
export const sortServices = (services, orderBy, orderDirection = "asc") => {
  if (!orderBy) return services;
  
  const data = [...services];
  const isAscending = orderDirection === "asc";
  
  data.sort((a, b) => {
    let valueA, valueB;
    
    switch (orderBy) {
      case "status":
        valueA = a.status || "";
        valueB = b.status || "";
        break;
      case "type":
        valueA = a.type || "";
        valueB = b.type || "";
        break;
      case "client":
        valueA = (a.client?.name || a.client || "").toLowerCase();
        valueB = (b.client?.name || b.client || "").toLowerCase();
        break;
      case "branch":
        valueA = (a.branch?.name || a.branch || "").toLowerCase();
        valueB = (b.branch?.name || b.branch || "").toLowerCase();
        break;
      case "technician":
        valueA = (a.technician?.name || a.technician || "").toLowerCase();
        valueB = (b.technician?.name || b.technician || "").toLowerCase();
        break;
      case "date":
        valueA = a.date ? new Date(a.date).getTime() : 0;
        valueB = b.date ? new Date(b.date).getTime() : 0;
        break;
      case "hour":
        valueA = a.hour || "";
        valueB = b.hour || "";
        break;
      default:
        return 0;
    }
    
    // Comparación
    if (valueA < valueB) {
      return isAscending ? -1 : 1;
    }
    if (valueA > valueB) {
      return isAscending ? 1 : -1;
    }
    return 0;
  });
  
  return data;
};

